import React from 'react';
import { Link } from 'react-router-dom';
import {
  Landmark, Globe2, Bot, HeartHandshake, ArrowRight, ArrowDown,
  MessageSquare, LineChart, Languages, Megaphone, Rocket, TrendingUp,
  Building2, Send
} from 'lucide-react';
import Seo from '../components/Seo';
import GlobalSolutions from '../components/GlobalSolutions';

const whyChoose = [
  {
    icon: <Landmark className="w-7 h-7" />,
    title: '日本のビジネス文化への深い理解',
    description: '日本企業がどのようにコミュニケーションを取り、信頼を築き、長期的な関係を育むのかを理解しています。',
  },
  {
    icon: <Globe2 className="w-7 h-7" />,
    title: '国際的な視点',
    description: 'バイリンガルでのコミュニケーションと、異なる文化をまたいで働いてきた経験があります。',
  },
  {
    icon: <Bot className="w-7 h-7" />,
    title: 'AIを活用したソリューション',
    description: '最新のAI技術を活用し、マーケティングコストを抑えながら、スピードと品質を高めます。',
  },
  {
    icon: <HeartHandshake className="w-7 h-7" />,
    title: '一社一社に寄り添うパートナーシップ',
    description: 'それぞれの目標とターゲット市場に合わせて、オーダーメイドの戦略をご提案します。',
  },
];

const comparison = [
  {
    label: 'Japanese Companies',
    title: '日本企業の皆様',
    subtitle: 'こんなことを実現したい',
    items: [
      '海外で商品・サービスを販売したい',
      '英語のWebサイトをつくりたい',
      '海外向けの広告を始めたい',
      '海外のお客様を見つけたい',
      'グローバルなブランディングを強化したい',
    ],
  },
  {
    label: 'International Companies',
    title: '海外企業の皆様',
    subtitle: 'こんなことを実現したい',
    items: [
      '日本市場に参入したい',
      '日本語のWebサイトをつくりたい',
      '日本のお客様にアプローチしたい',
      'マーケティングを日本向けにローカライズしたい',
      '日本国内のパートナーを見つけたい',
    ],
  },
];

const processSteps = [
  { num: 1, icon: <MessageSquare className="w-6 h-6" />, title: 'ご相談', en: 'Consultation' },
  { num: 2, icon: <LineChart className="w-6 h-6" />, title: '市場分析', en: 'Market Analysis' },
  { num: 3, icon: <Languages className="w-6 h-6" />, title: 'ローカライズ', en: 'Localization' },
  { num: 4, icon: <Megaphone className="w-6 h-6" />, title: 'デジタルマーケティング', en: 'Digital Marketing' },
  { num: 5, icon: <Rocket className="w-6 h-6" />, title: 'ローンチ', en: 'Launch' },
  { num: 6, icon: <TrendingUp className="w-6 h-6" />, title: '長期的な成長', en: 'Long-term Growth' },
];

const GlobalBusiness = () => {
  return (
    <div>
      <Seo
        title="Japan Market Entry & Global Expansion | Sakura Local Business Support"
        description="Helping Japanese businesses expand globally and international companies enter the Japanese market through bilingual consulting, AI marketing, localization, SEO, and digital strategy."
        keywords="Japan Market Entry,Japanese Localization,Global Expansion,Export Support,Business Consulting Japan,International Marketing,Bilingual Marketing,AI Marketing,Cross-border Business,SLBS,日本市場進出,海外進出支援"
      />

      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFF7FA 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center fade-in">
            <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase mb-6 px-5 py-2 rounded-full bg-white shadow-sm" style={{ color: '#C77A93' }}>
              Global Business Solutions
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ color: '#2C2C2C' }}>
              Connecting Japan<br />and the World.
            </h1>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
              海外進出を目指す日本企業と、日本市場への参入を目指す海外企業の双方を支援します。
              バイリンガル対応のコンサルティングとAIを活用したデジタルマーケティングで、国境を越えた成長をお手伝いします。
            </p>
          </div>
        </div>
      </section>

      {/* Global Business Solutions - two premium cards */}
      <GlobalSolutions background="#FFFFFF" />

      {/* Why Choose SLBS */}
      <section className="py-16 md:py-20" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
              Why Choose SLBS?
            </h2>
            <p className="text-gray-600">SLBSが選ばれる理由</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {whyChoose.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover-lift p-8">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: '#FFF7FA', color: '#C77A93' }}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#2C2C2C' }}>
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* We Help Businesses Grow Both Ways - infographic */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
              双方向でビジネスの成長を支援します
            </h2>
            <p className="text-gray-600">We Help Businesses Grow Both Ways</p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            {/* Left: Japan -> Global */}
            <div className="rounded-2xl p-8 text-center shadow-sm border border-gray-100" style={{ backgroundColor: '#FFF7FA' }}>
              <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-4 bg-white" style={{ color: '#C77A93' }}>
                <Building2 className="w-7 h-7" />
              </div>
              <p className="text-lg font-semibold" style={{ color: '#2C2C2C' }}>日本 Japan</p>
              <ArrowDown className="w-6 h-6 mx-auto my-3" style={{ color: '#C77A93' }} />
              <p className="text-gray-700">世界市場へ<br /><span className="text-sm text-gray-500">Global Markets</span></p>
            </div>

            {/* Center: SLBS */}
            <div className="rounded-3xl p-8 text-center shadow-lg" style={{ backgroundColor: '#2C2C2C' }}>
              <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: '#F7C9D3' }}>
                <Globe2 className="w-8 h-8" style={{ color: '#2C2C2C' }} />
              </div>
              <p className="text-2xl font-bold text-white mb-2">SLBS</p>
              <p className="text-sm text-gray-300">Connecting Both Directions</p>
            </div>

            {/* Right: Overseas -> Japan */}
            <div className="rounded-2xl p-8 text-center shadow-sm border border-gray-100" style={{ backgroundColor: '#FFF7FA' }}>
              <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-4 bg-white" style={{ color: '#C77A93' }}>
                <Globe2 className="w-7 h-7" />
              </div>
              <p className="text-lg font-semibold" style={{ color: '#2C2C2C' }}>海外 Overseas</p>
              <ArrowDown className="w-6 h-6 mx-auto my-3" style={{ color: '#C77A93' }} />
              <p className="text-gray-700">日本市場へ<br /><span className="text-sm text-gray-500">Japan</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Comparison */}
      <section className="py-16 md:py-20" style={{ backgroundColor: '#FFF7FA' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
              こんな課題を解決します
            </h2>
            <p className="text-gray-600">Service Comparison</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {comparison.map((col, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 md:p-10">
                <p className="text-sm font-medium tracking-wide mb-1" style={{ color: '#C77A93' }}>{col.label}</p>
                <h3 className="text-2xl font-bold mb-1" style={{ color: '#2C2C2C' }}>{col.title}</h3>
                <p className="text-sm text-gray-500 mb-6">{col.subtitle}</p>
                <ul className="space-y-3">
                  {col.items.map((item, i) => (
                    <li key={i} className="flex items-start">
                      <span className="mt-1 mr-3 flex-shrink-0 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#C77A93' }} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#2C2C2C' }}>
              ご支援の流れ
            </h2>
            <p className="text-gray-600">Our Process</p>
          </div>
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative text-center">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover-lift p-6 h-full">
                  <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-3" style={{ backgroundColor: '#F7C9D3', color: '#2C2C2C' }}>
                    {step.icon}
                  </div>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#C77A93' }}>STEP {step.num}</p>
                  <p className="font-semibold text-sm" style={{ color: '#2C2C2C' }}>{step.title}</p>
                  <p className="text-xs text-gray-400 mt-1">{step.en}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 -translate-y-1/2 w-5 h-5" style={{ color: '#F7C9D3' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision - Beyond Borders */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#2C2C2C' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white">
              Beyond Borders.
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              SLBSは、素晴らしいビジネスにはすべて、地理的な境界を越えて成長する機会があると信じています。
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              海外進出を目指す日本企業も、日本市場への参入を目指す海外企業も。
              私たちは、知識・テクノロジー・現地の専門性を通じて、皆様の成功をお手伝いします。
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20" style={{ backgroundColor: '#F7C9D3' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#2C2C2C' }}>
            Ready to Expand?
          </h2>
          <p className="text-lg mb-8" style={{ color: '#2C2C2C' }}>
            あなたのビジネスの目標について、お聞かせください。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center justify-center px-8 py-4 rounded-full text-lg font-semibold"
              data-testid="global-cta-jp"
            >
              <Send className="w-5 h-5 mr-2" />
              無料相談はこちら
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full text-lg font-semibold bg-white text-[#2C2C2C] hover:bg-gray-50 transition-colors"
              data-testid="global-cta-en"
            >
              Contact Us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlobalBusiness;
